import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Button,
  TextField,
  InputAdornment,
  CircularProgress,
  Divider,
} from '@mui/material';
import { Search, LocationOn, Architecture, ArrowForward } from '@mui/icons-material';
import { collection, query, getDocs, orderBy } from 'firebase/firestore';
import { db } from '@/services/firebase';
import { Chantier } from '@/types';
import PublicHeader from '@/components/PublicHeader';
import PublicFooter from '@/components/PublicFooter';
import { motion } from 'framer-motion';
import { t } from '@/utils/i18n-enhanced';

const ChantiersPage: React.FC = () => {
  const navigate = useNavigate();
  const [chantiers, setChantiers] = useState<Chantier[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchChantiers = async () => {
      try {
        const q = query(collection(db, 'chantiers'), orderBy('createdAt', 'desc'));
        const snapshot = await getDocs(q);
        setChantiers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Chantier)));
      } catch (error) {
        console.error('Error fetching chantiers:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchChantiers();
  }, []);

  const filteredChantiers = chantiers.filter(c => 
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.location?.city.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box sx={{ bgcolor: 'grey.50', minHeight: '100vh' }}>
      <PublicHeader />
      
      <Box sx={{ pt: 15, pb: 8, bgcolor: 'primary.main', color: 'white' }}>
        <Container maxWidth="lg">
          <Typography variant="h2" sx={{ fontWeight: 800, mb: 2, fontFamily: '"Playfair Display", serif' }}>
            {t('chantiers_title')}
          </Typography>
          <Typography variant="h5" sx={{ opacity: 0.9, mb: 4, maxWidth: 600 }}>
            {t('chantiers_subtitle')}
          </Typography>

          <TextField
            fullWidth
            placeholder={t('chantiers_search_placeholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ 
              bgcolor: 'white', 
              borderRadius: 2,
              '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
              boxShadow: 3
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress size={60} />
          </Box>
        ) : (
          <Grid container spacing={4}>
            {filteredChantiers.map((chantier) => (
              <Grid item xs={12} md={6} key={chantier.id}>
                <motion.div whileHover={{ y: -10 }} transition={{ duration: 0.3 }}>
                  <Card 
                    sx={{ 
                      borderRadius: 4, 
                      overflow: 'hidden', 
                      boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
                      cursor: 'pointer'
                    }}
                    onClick={() => navigate(`/chantiers/${chantier.id}`)}
                  >
                    <Box sx={{ position: 'relative', height: 280 }}>
                      <CardMedia
                        component="img"
                        image={chantier.heroVideoUrl || chantier.masterPlanUrl || "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&q=80"}
                        sx={{ height: '100%' }}
                      />
                      <Box sx={{ position: 'absolute', top: 20, right: 20 }}>
                        <Chip
                          label={`${chantier.progress.percentage}% ${t('chantiers_progress')}`}
                          color="primary"
                          sx={{ fontWeight: 700, px: 1 }}
                        />
                      </Box>
                    </Box>
                    <CardContent sx={{ p: 4 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box>
                          <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>{chantier.name}</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                            <LocationOn fontSize="small" />
                            <Typography variant="body2">{chantier.location?.city}</Typography>
                          </Box>
                        </Box>
                        <Architecture color="primary" />
                      </Box>
                      
                      <Typography variant="body2" color="text.secondary" paragraph sx={{ height: 40, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {chantier.description}
                      </Typography>

                      <Divider sx={{ my: 2 }} />
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          {t('chantiers_delivery')}: {chantier.progress.phase.toUpperCase()}
                        </Typography>
                        <Button endIcon={<ArrowForward />}>{t('chantiers_learn_more')}</Button>
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
            {filteredChantiers.length === 0 && (
              <Grid item xs={12}>
                <Box sx={{ py: 10, textAlign: 'center' }}>
                  <Typography variant="h6" color="text.secondary">{t('chantiers_no_results')}</Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        )}
      </Container>

      <PublicFooter />
    </Box>
  );
};

export default ChantiersPage;
